import { BaseService } from "./BaseService";
import { CommandService } from "./CommandService";
import { IDataRow, IODataResult } from "Entities";
import { Configuration } from "Configuration";
import {
    DropDownClient,
    DataRequestOption,
    DropDownRequestParameter,
    DropDownCommandResult,
    MultipleControlRequestParameter,
} from "./Client";
import CancellationToken from "cancellationtoken";

/**
 * Service for requesting comboboxdata.
 * @hidden
 */
export class DropDownService extends BaseService {
    /**
     * Get data for all comboboxes in an EditContext.
     * @param gridId Id of the grid.
     * @param options List of request options.
     * @param dataRow Current datarow in grid / form.
     */
    public async getAll(
        gridId: number,
        options: DataRequestOption[],
        dataRow: IDataRow,
        cancellationToken: CancellationToken
    ): Promise<{ [controlID: number]: IODataResult }> {
        const client = new DropDownClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();

        let param = <MultipleControlRequestParameter>(
            $.extend(CommandService.getRequestParameters(), { DataRow: { ...dataRow } }, { DataRequestOptions: options })
        );
        delete param.DataRow.__original;
        delete param.DataRow._events;
        delete param.DataRow._handlers;
        delete param.DataRow.dirtyFields;

        return this.handleResult(
            new Promise<{ [controlID: number]: IODataResult }>((resolve, reject) => {
                const xhr = client.get(
                    gridId,
                    param,
                    (res) => resolve(res),
                    (exception, reason) => reject(exception)
                );
                cancellationToken.onCancelled((reason) => {
                    xhr.abort();
                    reject(reason);
                });
            })
        );
    }

    /**
     * Get data for a single combobox.
     * @param gridId Id of the grid.
     * @param controlId Id of the combobox control.
     * @param option Request option.
     * @param dataRow Current datarow in grid / form.
     */
    public get(gridId: number, controlId: number, option: DataRequestOption, dataRow: IDataRow): Promise<DropDownCommandResult> {
        let client = new DropDownClient(Configuration.baseUrl);
        let params = <DropDownRequestParameter>(
            $.extend(CommandService.getRequestParameters(), { DataRow: { ...dataRow } }, { DataRequestOption: option })
        );

        delete params.DataRow.__original;
        delete params.DataRow._events;
        delete params.DataRow._handlers;
        delete params.DataRow.dirtyFields;
        client.beforeSend = this.getBeforeSend();

        return new Promise<DropDownCommandResult>((resolve, reject) => {
            client.getControl(
                gridId,
                controlId,
                params,
                (res) => resolve(res),
                (exc, res) => reject(exc)
            );
        });
    }

    /**
     * Get the index of an item in the combobox. Used for async comboboxes.
     * @param gridId Id of the grid.
     * @param controlId Id of the combobox control.
     * @param option Request option.
     * @param dataRow Current datarow in grid / form.
     */
    public mapValue(gridId: number, controlId: number, option: DataRequestOption, dataRow: IDataRow): Promise<DropDownCommandResult> {
        let params = <DropDownRequestParameter>(
            $.extend(CommandService.getRequestParameters(), { DataRow: { ...dataRow } }, { DataRequestOption: option })
        );
        let client = new DropDownClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();
        delete params.DataRow.__original;
        delete params.DataRow._events;
        delete params.DataRow._handlers;
        delete params.DataRow.dirtyFields;

        return this.handleResult(
            new Promise<DropDownCommandResult>((resolve, reject) => {
                client.mapValue(
                    gridId,
                    controlId,
                    params,
                    (res) => resolve(res),
                    (exc, res) => reject(exc)
                );
            })
        );
    }
}
